import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  makeStyles,
  Option,
  Spinner
} from "@fluentui/react-components";
import React from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { createLroHooks } from "@tdp/common/lro/hooks";
import { DeveloperPortalRootContext, useCommand } from "@tdp/core";

import { IAppListLro } from "../abstractions/IAppListLro";


const { useLro: useNewAppLro } = createLroHooks<IAppListLro>(
  'newAppCreationLro',
  { newAppId: '', isAppItemUpdated: false },
);


const useStyles = makeStyles({
  container: {
    margin: "12px 0px 24px 0px",
    display: "grid",
    gap: "18px",
  },
  field: {
    display: "grid",
    justifyItems: "start",
  },
  input: {
    width: "85%",
  }
});

export const AppListNewDialog: React.FC<
  {
    open: boolean,
    setOpen: (value: boolean) => void,
  }
> = ({ open, setOpen }) => {
  const classes = useStyles();
  const intl = useIntl();

  // Commands and LROs
  const createApp = useCommand((commands) => commands.appListCommands.createApp);
  const newAppLro = useNewAppLro([]);
  const navigate = useNavigate();
  const { services: { logger } } = React.useContext(DeveloperPortalRootContext);


  // Component level state
  const manifests = [
    { manifest: "Latest Stable (v1.22)", version: "1.22" },
    { manifest: "Public DevPreview (vdevPreview)", version: "devPreview" },
  ]
  const title = intl.formatMessage({ defaultMessage: `Create a new app` });
  const [activeManifest, setActiveManifest] = React.useState<string>(manifests[0].version);
  const [appName, setAppName] = React.useState<string>("");
  const [isDisabled, setIsDisabled] = React.useState<boolean>(false);

  const onCreateApp = () => {
    const payload = {
      appName: appName,
      shortName: appName,
      manifestVersion: activeManifest,
      version: "1.0.0",
      appStudioVersion: "2.0.0",
      colorIcon: "/images/default-app-icons/color_192x192.png",
      outlineIcon: "/images/default-app-icons/outline_32x32.png",
    };
    setIsDisabled(true);
    const lro = newAppLro.createLro();
    createApp({ lro, payload });
    logger.logUserBi({ telemetryMarker: 'apps-create-new-app', message: `User created a new app with manifest version: ${activeManifest}` });
  }

  React.useEffect(() => {
    if (newAppLro.lro?.state.isAppItemUpdated && newAppLro.lro.state.newAppId) {
      setIsDisabled(false);
      setAppName("");
      setOpen(false);
      navigate(`/apps/${newAppLro.lro.state.newAppId}/dashboard`);
    }
  }, [newAppLro.lro]);

  return (
    <Dialog open={open}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent className={classes.container}>
            <div className={classes.field}>
              <Field id={"name"} label={intl.formatMessage({ defaultMessage: "Name" })} required />
              <Input
                required={true}
                aria-labelledby={"name"}
                className={classes.input}
                placeholder={intl.formatMessage({ defaultMessage: "Enter the name of the app" })}
                onChange={(e) => { setAppName(e.target.value); }}
              />
            </div>
            <div className={classes.field}>
              <Field id={"manifest"} label={intl.formatMessage({ defaultMessage: "Manifest version" })} required />
              <Dropdown
                aria-required={true}
                aria-labelledby={"manifest"}
                defaultValue={manifests[0].manifest}
                className={classes.input}
                onOptionSelect={(_, data) => {
                  setActiveManifest(
                    manifests.find((m) => m.manifest === data.selectedOptions[0])?.version
                    ?? manifests[0].version
                  );
                }}
              >
                {manifests.map((option) => (
                  <Option key={option.version}>
                    {option.manifest}
                  </Option>
                ))}
              </Dropdown>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => { setOpen(false) }}>
              {intl.formatMessage({ defaultMessage: "Cancel" })}
            </Button>
            <Button
              icon={isDisabled ? <Spinner size="tiny" /> : null}
              appearance="primary"
              disabled={!appName || isDisabled}
              onClick={onCreateApp}>
              {intl.formatMessage({ defaultMessage: "Create" })}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );

}
